/**
 * CaptureSettingsDrawer - Slide-out drawer containing secondary capture controls.
 * Shows face progress, gallery, and navigation to face selection.
 * @module CaptureSettingsDrawer
 */

import {
  DEGREES_PER_CAPTURE_OPTIONS,
  calculateCapturesFromDegrees,
  type DegreesPerCapture
} from '@dice-capture/shared/types/dice-registry';
import { ChevronRight } from 'lucide-react';
import { FaceGallery } from './FaceGallery';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { cn } from '@/lib/utils';
import type { FaceImage } from '@/stores/diceRegistryStore';

/**
 * Props for CaptureSettingsDrawer component.
 */
export interface CaptureSettingsDrawerProps {
  /** Whether drawer is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Current face number being captured */
  currentFace: number;
  /** Total number of faces for this dice type */
  totalFaces: number;
  /** Images captured for current face */
  currentFaceImages: FaceImage[];
  /** Minimum images required per face */
  minImagesPerFace: number;
  /** Current degrees per capture setting (TICKET-117) */
  degreesPerCapture: DegreesPerCapture;
  /** Callback when degrees setting changes (TICKET-117) */
  onDegreesChange: (degrees: DegreesPerCapture) => void;
  /** Callback to navigate to face selection screen */
  onSelectFace: () => void;
  /** Callback when an image is deleted */
  onDeleteImage: (imageId: string) => void;
  /** Callback when a thumbnail image is clicked — parent handles lightbox */
  onImageClick?: (image: FaceImage) => void;
  /** Callback to add more images (triggers capture) */
  onAddMoreImages: () => void;
  /** Whether minimum images have been captured */
  hasMinimumImages: boolean;
  /** Whether this is the last face */
  isLastFace: boolean;
  /** Whether actions are loading */
  isLoading: boolean;
  /** TICKET-180: Whether unlimited mode is active (no minimum enforced) */
  isUnlimitedMode: boolean;
}

/**
 * Slide-out drawer containing secondary capture controls.
 * Slides from bottom in portrait, right in landscape.
 */
export function CaptureSettingsDrawer({
  open,
  onOpenChange,
  currentFace,
  totalFaces,
  currentFaceImages,
  minImagesPerFace,
  degreesPerCapture,
  onDegreesChange,
  onSelectFace,
  onDeleteImage,
  onImageClick,
  onAddMoreImages,
  hasMinimumImages,
  isLastFace,
  isLoading,
  isUnlimitedMode
}: CaptureSettingsDrawerProps) {
  const isLandscape = useIsLandscape();
  const imageCount = currentFaceImages.length;
  const progressPercent = Math.min(100, (imageCount / minImagesPerFace) * 100);

  // Calculate captures from current degrees setting
  const capturesPerFace = calculateCapturesFromDegrees(degreesPerCapture);

  /**
   * Handle degree button click - set new degrees value
   */
  const handleDegreeSelect = (degrees: DegreesPerCapture) => {
    onDegreesChange(degrees);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={isLandscape ? 'right' : 'bottom'}>
      <DrawerContent
        direction={isLandscape ? 'right' : 'bottom'}
        className={cn(
          isLandscape ? 'w-72' : 'max-h-[60vh]'
        )}
      >
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Rotation per Capture Control (TICKET-117) */}
          <div className="space-y-2 pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Rotation per capture</label>
              <span className="text-sm font-medium text-primary">{degreesPerCapture}deg</span>
            </div>
            {/* Segmented button group for degree options */}
            <div className="flex flex-wrap gap-1">
              {DEGREES_PER_CAPTURE_OPTIONS.map((degrees) => (
                <button
                  key={degrees}
                  type="button"
                  onClick={() => handleDegreeSelect(degrees)}
                  className={cn(
                    'px-2 py-1 text-xs font-medium rounded-md transition-colors',
                    degrees === degreesPerCapture
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                  )}
                  aria-pressed={degrees === degreesPerCapture}
                  aria-label={`${degrees} degrees per capture`}
                >
                  {degrees}deg
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {degreesPerCapture}deg = {capturesPerFace} captures per face
            </p>
          </div>

          {/* Select Face CTA - Prominent button */}
          <Button
            variant="accent"
            className="w-full justify-between"
            onClick={() => {
              onSelectFace();
              onOpenChange(false);
            }}
            disabled={isLoading}
          >
            <span>Select Different Face</span>
            <ChevronRight className="w-4 h-4" />
          </Button>

          {/* Face Progress Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-bold text-lg">Face {currentFace}</span>
                <span className="text-muted-foreground ml-2 text-sm">
                  of {totalFaces}
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm">{imageCount} images</span>
                {hasMinimumImages && (
                  <span className="text-green-500 text-xs ml-1">(ready)</span>
                )}
              </div>
            </div>

            {/* Progress bar — hidden in unlimited mode */}
            {!isUnlimitedMode && (
              <div
                className="w-full bg-secondary rounded-full h-1.5"
                role="progressbar"
                aria-valuenow={progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${imageCount} of ${minImagesPerFace} images captured`}
              >
                <div
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-300',
                    hasMinimumImages ? 'bg-green-500' : 'bg-primary'
                  )}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {isUnlimitedMode
                ? `${imageCount} images captured (no limit)`
                : hasMinimumImages
                  ? 'Minimum images captured. Add more for better accuracy.'
                  : `Capture ${minImagesPerFace - imageCount} more images`}
            </p>
          </div>

          {/* Face Gallery */}
          <FaceGallery
            faceNumber={currentFace}
            images={currentFaceImages}
            onDeleteImage={onDeleteImage}
            onImageClick={onImageClick}
          />

          {/* Add More Images button */}
          {hasMinimumImages && !isLastFace && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                onAddMoreImages();
                onOpenChange(false);
              }}
              disabled={isLoading}
            >
              Add More Images
            </Button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
